import React, { useEffect, useState } from 'react';
import { Building2, Upload, Trash2, Loader2, Check, X, Save, FileSpreadsheet, Layers, Settings2, PanelLeft } from 'lucide-react';
import api from '@/lib/axios';
import { useTenant } from '@/contexts/TenantContext';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import ImikelImportDialog from './ImikelImportDialog';
import GenericImportDialog from './GenericImportDialog';
import BulkImportDialog from './BulkImportDialog';
import DiscountSchemeDialog from './DiscountSchemeDialog';
import InvoiceSettingsDialog from './InvoiceSettingsDialog';
import SidebarMenuDialog from './SidebarMenuDialog';

/**
 * Mandanten-Verwaltung (Super-Admin).
 * Listet alle Mandanten, zeigt Logo pro Mandant und erlaubt Upload/Löschen.
 *
 * Zugriffskontrolle: Frontend blendet die Route im Menü nur für s-admin ein.
 * Backend erzwingt zusätzlich 403 wenn nicht berechtigt.
 */
export default function TenantsAdminPage() {
  const { isSuperAdmin, refresh: refreshTenantContext } = useTenant();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingId, setUploadingId] = useState(null);
  const [prefixDraft, setPrefixDraft] = useState({}); // { [tenantId]: string }
  const [savingPrefixId, setSavingPrefixId] = useState(null);
  const [importTenant, setImportTenant] = useState(null); // tenant currently being imported into
  const [genericImportTenant, setGenericImportTenant] = useState(null);
  const [bulkImportTenant, setBulkImportTenant] = useState(null);
  const [togglingTwoStageId, setTogglingTwoStageId] = useState(null);
  const [schemeTenant, setSchemeTenant] = useState(null);
  const [invoiceSettingsTenant, setInvoiceSettingsTenant] = useState(null);
  const [sidebarMenuTenant, setSidebarMenuTenant] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/tenants');
      const list = res.data || [];
      setTenants(list);
      // Seed the editable draft with the current prefix
      setPrefixDraft(
        list.reduce((acc, t) => ({ ...acc, [t.id]: t.invoice_prefix || '' }), {}),
      );
    } catch (err) {
      toast.error('Fehler beim Laden der Mandanten');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleUpload = async (tenantId, file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Datei zu groß (max. 2 MB)');
      return;
    }
    setUploadingId(tenantId);
    try {
      const form = new FormData();
      form.append('file', file);
      await api.post(`/tenants/${tenantId}/logo`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Logo erfolgreich hochgeladen');
      await load();
      await refreshTenantContext();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload fehlgeschlagen');
    } finally {
      setUploadingId(null);
    }
  };

  const handleDelete = async (tenantId) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Logo wirklich löschen?')) return;
    try {
      await api.delete(`/tenants/${tenantId}/logo`);
      toast.success('Logo entfernt');
      await load();
      await refreshTenantContext();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Löschen fehlgeschlagen');
    }
  };

  const handleSavePrefix = async (tenantId) => {
    const value = (prefixDraft[tenantId] || '').trim();
    setSavingPrefixId(tenantId);
    try {
      await api.put(`/tenants/${tenantId}`, { invoice_prefix: value || null });
      toast.success('Rechnungs-Präfix gespeichert');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setSavingPrefixId(null);
    }
  };

  const handleToggleTwoStage = async (tenant) => {
    const next = !tenant.two_stage_discount;
    setTogglingTwoStageId(tenant.id);
    try {
      await api.put(`/tenants/${tenant.id}`, { two_stage_discount: next });
      toast.success(
        next
          ? 'Two-Stage Rabattlogik aktiviert'
          : 'Two-Stage Rabattlogik deaktiviert',
      );
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setTogglingTwoStageId(null);
    }
  };

  if (!isSuperAdmin) {
    return (
      <Layout>
        <div className="p-8">
          <Card className="p-8 text-center">
            <X className="w-10 h-10 text-red-500 mx-auto mb-2" />
            <h2 className="text-lg font-semibold">Kein Zugriff</h2>
            <p className="text-sm text-slate-500">
              Die Mandantenverwaltung ist nur für Super-Administratoren verfügbar.
            </p>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-8 max-w-5xl mx-auto" data-testid="tenants-admin-page">
        <div className="flex items-center gap-3 mb-6">
          <Building2 className="w-7 h-7 text-brand-red" strokeWidth={1.5} />
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">Mandantenverwaltung</h1>
            <p className="text-sm text-slate-500">
              Logo, Stammdaten und Seitenmenü (ein-/ausblenden) pro Standort
            </p>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {tenants.map((t) => {
              const logoSrc = t.logo_url
                ? `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '')}${t.logo_url}?v=${Date.now()}`
                : null;
              const isUploading = uploadingId === t.id;
              return (
                <Card
                  key={t.id}
                  className="p-5"
                  data-testid={`tenant-card-${t.slug}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <h3 className="font-semibold text-slate-900 truncate">
                        {t.name}
                      </h3>
                      <p className="text-xs text-slate-500 font-mono mt-0.5">
                        {t.slug}
                      </p>
                      {t.description && (
                        <p className="text-sm text-slate-600 mt-2">{t.description}</p>
                      )}
                    </div>
                    {t.is_active ? (
                      <span className="inline-flex items-center gap-1 text-[10px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
                        <Check className="w-3 h-3" /> aktiv
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[10px] font-medium text-slate-500 bg-slate-50 border border-slate-200 px-2 py-0.5 rounded-full">
                        inaktiv
                      </span>
                    )}
                  </div>

                  <div className="mt-4 rounded-md border border-dashed border-slate-300 bg-slate-50 h-28 flex items-center justify-center overflow-hidden">
                    {logoSrc ? (
                      <img
                        src={logoSrc}
                        alt={`Logo ${t.name}`}
                        className="max-h-24 max-w-full object-contain"
                        data-testid={`tenant-logo-preview-${t.slug}`}
                      />
                    ) : (
                      <div className="text-center text-slate-400">
                        <Building2 className="w-7 h-7 mx-auto mb-1" strokeWidth={1.3} />
                        <p className="text-xs">Kein Logo hinterlegt</p>
                      </div>
                    )}
                  </div>

                  <div className="mt-4 flex items-center gap-2">
                    <label className="flex-1">
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/webp,image/svg+xml"
                        className="hidden"
                        data-testid={`tenant-logo-upload-${t.slug}`}
                        onChange={(e) => handleUpload(t.id, e.target.files?.[0])}
                        disabled={isUploading}
                      />
                      <Button
                        asChild
                        size="sm"
                        variant="outline"
                        className="w-full cursor-pointer"
                        disabled={isUploading}
                      >
                        <span>
                          {isUploading ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Lade hoch…
                            </>
                          ) : (
                            <>
                              <Upload className="w-4 h-4 mr-2" />
                              {t.has_logo ? 'Logo ersetzen' : 'Logo hochladen'}
                            </>
                          )}
                        </span>
                      </Button>
                    </label>
                    {t.has_logo && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-600 hover:bg-red-50"
                        onClick={() => handleDelete(t.id)}
                        disabled={isUploading}
                        data-testid={`tenant-logo-delete-${t.slug}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>

                  <p className="mt-2 text-[11px] text-slate-400">
                    PNG, JPG, WebP oder SVG · max. 2 MB
                  </p>

                  <div className="mt-4 pt-4 border-t border-slate-100">
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      data-testid={`tenant-sidebar-menu-${t.slug}`}
                      onClick={() => setSidebarMenuTenant(t)}
                    >
                      <PanelLeft className="w-3.5 h-3.5 mr-1" />
                      Seitenmenü (ein-/ausblenden)
                      {(t.sidebar_menu?.hidden?.length || 0) > 0 && (
                        <span className="ml-1 text-[10px] text-slate-500">
                          · {t.sidebar_menu.hidden.length} ausgeblendet
                        </span>
                      )}
                    </Button>
                  </div>

                  <div className="mt-5 pt-4 border-t border-slate-100">
                    <Label
                      htmlFor={`prefix-${t.slug}`}
                      className="text-xs font-semibold text-slate-700"
                    >
                      Rechnungsnummer-Präfix
                    </Label>
                    <p className="text-[11px] text-slate-500 mt-0.5 mb-2">
                      Vorschau:{' '}
                      <span className="font-mono text-slate-700">
                        R-{(prefixDraft[t.id] || '').trim() ? `${(prefixDraft[t.id] || '').trim()}-` : ''}
                        {new Date().getFullYear()}-00001
                      </span>
                    </p>
                    <div className="flex items-center gap-2">
                      <Input
                        id={`prefix-${t.slug}`}
                        data-testid={`tenant-prefix-input-${t.slug}`}
                        value={prefixDraft[t.id] ?? ''}
                        onChange={(e) =>
                          setPrefixDraft((s) => ({ ...s, [t.id]: e.target.value }))
                        }
                        maxLength={10}
                        placeholder="z. B. WF"
                        className="h-8 text-sm font-mono uppercase"
                      />
                      <Button
                        size="sm"
                        data-testid={`tenant-prefix-save-${t.slug}`}
                        disabled={
                          savingPrefixId === t.id ||
                          (prefixDraft[t.id] ?? '') === (t.invoice_prefix ?? '')
                        }
                        onClick={() => handleSavePrefix(t.id)}
                      >
                        {savingPrefixId === t.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Save className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-slate-100">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <Label className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
                          <Layers className="w-3.5 h-3.5 text-brand-red" />
                          Two-Stage Rabattlogik
                        </Label>
                        <p className="text-[11px] text-slate-500 mt-0.5">
                          {t.two_stage_discount
                            ? 'Aktiv: Mehrfächererm. (intra-Kind) + Geschwistererm. (inter-Kind)'
                            : 'Inaktiv: globaler Single-Rank über alle Familienmitglieder'}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant={t.two_stage_discount ? 'default' : 'outline'}
                        onClick={() => handleToggleTwoStage(t)}
                        disabled={togglingTwoStageId === t.id}
                        data-testid={`tenant-two-stage-toggle-${t.slug}`}
                        className={
                          t.two_stage_discount
                            ? 'bg-brand-red hover:bg-brand-red/90 text-white'
                            : ''
                        }
                      >
                        {togglingTwoStageId === t.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : t.two_stage_discount ? (
                          <>
                            <Check className="w-3.5 h-3.5 mr-1" /> An
                          </>
                        ) : (
                          'Aus'
                        )}
                      </Button>
                    </div>

                    <div className="mt-3 flex items-center justify-between gap-2">
                      <div className="min-w-0 flex-1 text-[11px] text-slate-500">
                        Schema:{' '}
                        <span className="font-mono text-slate-700">
                          {t.discount_scheme?.type || '— (Greven-Default)'}
                        </span>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setSchemeTenant(t)}
                        data-testid={`tenant-scheme-edit-${t.slug}`}
                      >
                        <Settings2 className="w-3.5 h-3.5 mr-1" />
                        Schema bearbeiten
                      </Button>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-slate-100">
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      data-testid={`tenant-invoice-settings-${t.slug}`}
                      onClick={() => setInvoiceSettingsTenant(t)}
                    >
                      <Settings2 className="w-3.5 h-3.5 mr-1" />
                      Rechnungsangaben (Anschrift · Steuer · Bank)
                    </Button>
                  </div>
                  <div className="mt-4 pt-4 border-t border-slate-100 space-y-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      data-testid={`tenant-import-${t.slug}`}
                      onClick={() => setImportTenant(t)}
                    >
                      <FileSpreadsheet className="w-4 h-4 mr-2" />
                      iMikel-Daten importieren
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full text-slate-600"
                      data-testid={`tenant-generic-import-${t.slug}`}
                      onClick={() => setGenericImportTenant(t)}
                    >
                      CSV/XLSX Mapper (Schüler / Lehrer / Fächer)
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full text-emerald-700"
                      data-testid={`tenant-bulk-import-${t.slug}`}
                      onClick={() => setBulkImportTenant(t)}
                    >
                      iMikel Bulk-Import (ZIP)
                    </Button>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      <ImikelImportDialog
        open={!!importTenant}
        onOpenChange={(v) => !v && setImportTenant(null)}
        tenant={importTenant}
        onImported={load}
      />

      <GenericImportDialog
        open={!!genericImportTenant}
        onOpenChange={(v) => !v && setGenericImportTenant(null)}
        tenant={genericImportTenant}
        onImported={load}
      />

      <BulkImportDialog
        open={!!bulkImportTenant}
        onOpenChange={(v) => !v && setBulkImportTenant(null)}
        tenant={bulkImportTenant}
        onImported={load}
      />

      <DiscountSchemeDialog
        open={!!schemeTenant}
        onOpenChange={(v) => !v && setSchemeTenant(null)}
        tenant={schemeTenant}
        onSaved={load}
      />

      <InvoiceSettingsDialog
        tenant={invoiceSettingsTenant}
        onClose={() => setInvoiceSettingsTenant(null)}
      />

      <SidebarMenuDialog
        tenant={sidebarMenuTenant}
        onClose={() => setSidebarMenuTenant(null)}
        onSaved={async () => {
          await load();
          await refreshTenantContext();
        }}
      />
    </Layout>
  );
}
